/**
 * PINpiBulkUpload Component
 * Handles CSV upload for bulk NPI PI data entry
 */

import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Loader2 
} from 'lucide-react';
import {
  downloadPICSVTemplate,
  bulkUploadPIData,
  parseCSVToPIData,
  type PICalculatorData,
  type PIBulkUploadResult,
} from '@/lib/pi-calculator-utils';

interface PINpiBulkUploadProps {
  practiceId: string;
  /** Viewing program year -- uploads and templates are scoped to this year. */
  year: number;
  onUploadComplete: () => void;
  /** When true (e.g. locked year), the upload entry point is disabled. */
  disabled?: boolean;
}

export function PINpiBulkUpload({ practiceId, year, onUploadComplete, disabled = false }: PINpiBulkUploadProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<PICalculatorData[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<PIBulkUploadResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setParseError(null);
    setUploadResult(null);

    try {
      const content = await selectedFile.text();
      const data = parseCSVToPIData(content);
      
      if (data.length === 0) {
        setParseError('No valid NPI records found in the CSV file.');
        setParsedData([]);
        return;
      }

      setParsedData(data);
    } catch (error) {
      setParseError('Failed to parse CSV file. Please check the format.');
      setParsedData([]);
    }
  };

  const handleDownloadTemplate = async () => {
    await downloadPICSVTemplate(practiceId, year);
  };

  const handleUpload = async () => {
    if (parsedData.length === 0) return;

    setIsUploading(true);
    try {
      const result = await bulkUploadPIData(practiceId, parsedData, year);
      setUploadResult(result);
      
      if (result && result.errors === 0) {
        onUploadComplete();
      }
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setFile(null);
    setParsedData([]);
    setUploadResult(null);
    setParseError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetUpload = () => {
    setFile(null);
    setParsedData([]);
    setUploadResult(null);
    setParseError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => open ? setIsOpen(true) : handleClose()}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          <Upload className="w-4 h-4 mr-2" />
                Import PI Data for All Providers
        </Button>
      </DialogTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-xs">
            <p>Download a template, enter PI data for multiple providers, then upload to apply changes</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Bulk Upload NPI PI Data</DialogTitle>
          <DialogDescription>
            Upload a CSV file to update PI data for multiple NPIs at once.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Template Download */}
          <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/50">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-8 h-8 text-muted-foreground" />
              <div>
                <p className="font-medium">Download CSV Template</p>
                <p className="text-sm text-muted-foreground">
                  Get a template with all required columns and example data
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={handleDownloadTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>Upload CSV File</Label>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              disabled={isUploading}
            />
          </div>

          {/* Parse Error */}
          {parseError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Parse Error</AlertTitle>
              <AlertDescription>{parseError}</AlertDescription>
            </Alert>
          )}

          {/* Preview */}
          {parsedData.length > 0 && !uploadResult && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Preview ({parsedData.length} NPIs found)</Label>
                <Button variant="ghost" size="sm" onClick={resetUpload}>
                  Clear
                </Button>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-48 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>NPI</TableHead>
                        <TableHead>Prerequisites</TableHead>
                        <TableHead>CEHRT ID</TableHead>
                        <TableHead>Period Start</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parsedData.slice(0, 10).map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono">{row.npi}</TableCell>
                          <TableCell>
                            {row.pi_pphi_1 && row.pi_pphi_2 && row.pi_infblo_1 && row.pi_oncdir_1 ? (
                              <Badge variant="outline" className="text-emerald-600">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                All Yes
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-600">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                Incomplete
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {row.cehrt_id || '—'}
                          </TableCell>
                          <TableCell>{row.performance_start || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {parsedData.length > 10 && (
                  <div className="p-2 text-center text-sm text-muted-foreground border-t bg-muted/50">
                    ... and {parsedData.length - 10} more NPIs
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Uploading {parsedData.length} NPIs...</span>
              </div>
              <Progress value={50} className="h-2" />
            </div>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <div className="space-y-4">
              {uploadResult.errors === 0 ? (
                <Alert className="border-emerald-500/30 bg-emerald-500/5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <AlertTitle className="text-emerald-700">Upload Successful</AlertTitle>
                  <AlertDescription className="text-emerald-600">
                    Successfully uploaded PI data for {uploadResult.processed} NPIs.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertTitle>Upload Completed with Errors</AlertTitle>
                  <AlertDescription>
                    {uploadResult.processed} NPIs processed, {uploadResult.errors} errors.
                  </AlertDescription>
                </Alert>
              )}

              {/* Results Summary */}
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-48 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>NPI</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Score</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {uploadResult.results.map((result, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono">{result.npi}</TableCell>
                          <TableCell>
                            {result.success ? (
                              <Badge variant="outline" className="text-emerald-600">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Success
                              </Badge>
                            ) : (
                              <Badge variant="destructive">
                                <XCircle className="w-3 h-3 mr-1" />
                                Failed
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>{result.score.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      {uploadResult.errorDetails.map((error, idx) => (
                        <TableRow key={`error-${idx}`}>
                          <TableCell className="font-mono">{error.row.npi || 'Unknown'}</TableCell>
                          <TableCell>
                            <Badge variant="destructive">
                              <XCircle className="w-3 h-3 mr-1" />
                              Error
                            </Badge>
                          </TableCell>
                          <TableCell className="text-destructive text-sm">
                            {error.error}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {uploadResult ? (
            <>
              <Button variant="outline" onClick={resetUpload}>
                Upload Another
              </Button>
              <Button onClick={handleClose}>
                Done
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                onClick={handleUpload} 
                disabled={parsedData.length === 0 || isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload {parsedData.length} NPIs
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}





